import React, { RefObject, useEffect, useState } from 'react';
import {
  DragDropContext,
  DragStart,
  DropResult,
  Droppable,
} from '@hello-pangea/dnd';
import { UseMutateFunction } from 'react-query';
import { useCategoryCoordinates } from 'src/components/pages/Categories/hooks';
import {
  CategoryType,
  Exact,
  UpdateCategoryOrderType,
  UpdateCategoryOrdersMutation,
} from 'src/graphql';
import { useWindowDimensions } from 'src/hooks';
import { HumanReadableLanguages } from 'src/types';
import { CategoryAndOrder } from '../Category/CategoryAndOrder';
import { UpdateCategoryModal } from '../UpdateCategoryModal/UpdateCategoryModal';
import {
  CategoriesListContainer,
  CategoryListStyled,
} from './CategoryList.styles';
import { handleOnSaveTitle } from './helpers';

interface Props {
  categories: CategoryType[];
  currentCategory: CategoryType | null;
  setCurrentCategory: React.Dispatch<React.SetStateAction<CategoryType | null>>;
  newCategory: number | null;
  newCategoryRef?: RefObject<HTMLDivElement | null>;
  networkError: boolean;
  setNetworkError: React.Dispatch<React.SetStateAction<boolean>>;
  updateCategory: () => void;
  isCurrentCategoryChanged: boolean;
  setShowConfirmChangesModal: (value: boolean) => void;
  updateCategoryLocale: (
    id: number,
    locale: string,
    name: string,
    description: string,
  ) => void;
  setClickedCategory: React.Dispatch<React.SetStateAction<CategoryType | null>>;
  updateOrder: UseMutateFunction<
    UpdateCategoryOrdersMutation,
    unknown,
    Exact<{
      categories: UpdateCategoryOrderType | UpdateCategoryOrderType[];
      draggedCategoryId: number;
    }>,
    unknown
  >;
  openIds: number[];
  setOpenIds: React.Dispatch<React.SetStateAction<number[]>>;
  updateCategoryModalOpen?: boolean;
  setUpdateCategoryModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  renderUpdateCategoryModal?: boolean;
}

const currentLocale: keyof typeof HumanReadableLanguages = 'en-GB';

export const CategoryList = ({
  categories,
  currentCategory,
  setCurrentCategory,
  newCategory,
  newCategoryRef,
  updateCategory,
  updateCategoryLocale,
  isCurrentCategoryChanged,
  setShowConfirmChangesModal,
  setClickedCategory,
  networkError,
  setNetworkError,
  updateOrder,
  openIds = [],
  setOpenIds,
  updateCategoryModalOpen: updateCategoryModalOpenProp,
  setUpdateCategoryModalOpen: setUpdateCategoryModalOpenProp,
  renderUpdateCategoryModal = true,
}: Props) => {
  let unnamedCategoryCount = 0;
  const [internalUpdateCategoryModalOpen, setInternalUpdateCategoryModalOpen] =
    useState(false);
  const updateCategoryModalOpen =
    updateCategoryModalOpenProp ?? internalUpdateCategoryModalOpen;
  const setUpdateCategoryModalOpen =
    setUpdateCategoryModalOpenProp ?? setInternalUpdateCategoryModalOpen;
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const { width, height } = useWindowDimensions();

  const {
    categoryListontainerRef,
    setRef,
    categoryJoinerCoordinates,
    recalculateCoordinates,
  } = useCategoryCoordinates({ categories, draggingId });

  useEffect(() => {
    recalculateCoordinates();
  }, [width, height, categories, recalculateCoordinates]);

  const categoryNames = categories.map(
    (category) => category.locales?.[currentLocale]?.name,
  );

  const onDragStart = ({ draggableId }: DragStart) => {
    setDraggingId(draggableId);
  };

  const onDragEnd = (result: DropResult) => {
    setDraggingId(null);
    if (!result.destination) {
      return;
    }
    const items = [...categories];
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    const newCategories = items.map((item, index) => ({
      ...item,
      order: index,
    }));
    updateOrder({
      draggedCategoryId: reorderedItem.id,
      categories: newCategories.map((item) => ({
        id: item.id.toString(),
        order: item.order,
      })),
    });
  };

  return (
    <CategoryListStyled
      data-testid={'category-list'}
      ref={categoryListontainerRef}
    >
      <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <Droppable droppableId="categories">
          {(provided, snapshot) => (
            <CategoriesListContainer
              rows={categories.length}
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {categories?.map((category, index) => {
                if (!category.locales?.[currentLocale]?.name) {
                  unnamedCategoryCount += 1;
                }
                const isCurrentCategory = Boolean(
                  currentCategory && category.id === currentCategory.id,
                );
                const onClickCategory = () => {
                  if (!isCurrentCategoryChanged) {
                    openIds?.includes(category.id)
                      ? setOpenIds(openIds.filter((id) => id !== category.id))
                      : setOpenIds([...openIds, category.id]);
                    setCurrentCategory(isCurrentCategory ? null : category);
                  } else {
                    setShowConfirmChangesModal(true);
                    setClickedCategory(category);
                  }
                };

                return (
                  <CategoryAndOrder
                    key={`category-accordion-${category.id}`}
                    category={category}
                    currentLocale={currentLocale}
                    setCurrentCategory={setCurrentCategory}
                    currentCategory={currentCategory}
                    isCurrentCategory={isCurrentCategory}
                    unnamedCategoryCount={unnamedCategoryCount}
                    updateCategory={updateCategory}
                    setUpdateCategoryModalOpen={setUpdateCategoryModalOpen}
                    newCategory={newCategory}
                    isCurrentCategoryChanged={isCurrentCategoryChanged}
                    newCategoryRef={newCategoryRef}
                    index={index}
                    isDragDisabled={openIds?.includes(category.id)}
                    isDragging={
                      snapshot.draggingOverWith === category.id.toString()
                    }
                    onClickCategory={onClickCategory}
                    onSaveTitle={(id, name, setName) =>
                      handleOnSaveTitle({
                        id,
                        name,
                        category,
                        setName,
                        currentCategory,
                        currentLocale,
                        isCurrentCategory,
                        setCurrentCategory,
                        updateCategoryLocale,
                        categories,
                      })
                    }
                    categoryJoinerCoordinates={categoryJoinerCoordinates}
                    setRef={setRef}
                    recalculateCoordinates={recalculateCoordinates}
                  />
                );
              })}
              {provided.placeholder}
            </CategoriesListContainer>
          )}
        </Droppable>
      </DragDropContext>
      {renderUpdateCategoryModal &&
        currentCategory &&
        updateCategoryModalOpen && (
          <UpdateCategoryModal
            currentCategory={currentCategory}
            categoryNames={categoryNames}
            updateCategoryModalOpen={updateCategoryModalOpen}
            setUpdateCategoryModalOpen={setUpdateCategoryModalOpen}
            setCurrentCategory={setCurrentCategory}
            updateCategoryLocale={updateCategoryLocale}
            networkError={networkError}
            setNetworkError={setNetworkError}
          />
        )}
    </CategoryListStyled>
  );
};
